import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Users, Building2, Sliders, ArrowRight, User, FileDown, Plus } from 'lucide-react'
import { apiService, DashboardStats, RecentContact, RecentCompany } from '@/services/api'
import { useToast } from '@/hooks/use-toast'
import { useNavigate } from 'react-router-dom'
import { useSettings } from '@/hooks/use-settings'

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentContacts, setRecentContacts] = useState<RecentContact[]>([])
  const [recentCompanies, setRecentCompanies] = useState<RecentCompany[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()
  const navigate = useNavigate()
  const { settings, loading: settingsLoading } = useSettings()

  // Charger toutes les données au montage
  useEffect(() => {
    const loadData = async () => {
      // Attendre que les settings soient chargés
      if (settingsLoading) return

      try {
        setLoading(true)
        
        // Charger les statistiques + derniers contacts et entreprises ajoutés en parallèle
        const [statsResponse, contactsResponse, companiesResponse] = await Promise.all([
          apiService.getDashboardStats(),
          apiService.getRecentContacts(settings.elementsPerPageDashboard),
          apiService.getRecentCompanies(settings.elementsPerPageDashboard)
        ])
        
        if (statsResponse.success && statsResponse.data) {
          setStats(statsResponse.data)
        } else {
          toast({
            title: "Erreur",
            description: "Impossible de charger les statistiques",
            variant: "destructive",
          })
        }

        if (contactsResponse.success && contactsResponse.data) {
          setRecentContacts(contactsResponse.data)
        }

        if (companiesResponse.success && companiesResponse.data) {
          setRecentCompanies(companiesResponse.data)
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
        toast({
          title: "Erreur",
          description: "Erreur lors du chargement des données",
          variant: "destructive",
        })
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [toast, settings.elementsPerPageDashboard, settingsLoading])

  // Fonctions de navigation
  const handleNavigateToContacts = () => {
    navigate('/contacts')
  }

  const handleNavigateToCompanies = () => {
    navigate('/companies')
  }

  const handleNavigateToCustomFields = () => {
    navigate('/custom-fields')
  }

  const handleNavigateToExport = () => {
    navigate('/export')
  }

  // Fonction pour obtenir les initiales d'un nom
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  // Fonction pour obtenir l'initiale d'une entreprise
  const getCompanyInitial = (name: string) => {
    return name.charAt(0).toUpperCase()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="text-sm text-gray-500 mt-1">Vue d'ensemble de votre activité</p>
      </div>

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Grande carte Contacts (dégradé) */}
        <div className="md:row-span-2 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-6 flex flex-col justify-between min-h-[220px]">
          <div>
            <div className="flex items-center gap-2 text-blue-100">
              <Users className="w-5 h-5" />
              <span className="text-sm font-medium">Contacts</span>
            </div>
            <p className="text-5xl font-bold mt-3">
              {loading ? (
                <span className="inline-block animate-pulse bg-white/30 h-12 w-32 rounded"></span>
              ) : (
                (stats?.contacts || 0).toLocaleString('fr-FR')
              )}
            </p>
          </div>
          <Button
            className="bg-white/15 hover:bg-white/25 text-white justify-center mt-4 backdrop-blur"
            onClick={handleNavigateToContacts}
          >
            Voir tous les contacts <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Entreprises */}
        <div className="rounded-2xl bg-white ring-1 ring-gray-200 p-5">
          <div className="flex items-center gap-2 text-emerald-600">
            <Building2 className="w-5 h-5" />
            <span className="text-sm font-medium text-gray-600">Entreprises</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '—' : (stats?.companies || 0).toLocaleString('fr-FR')}
          </p>
          <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 p-0 h-auto mt-1" onClick={handleNavigateToCompanies}>
            Voir toutes les entreprises
          </Button>
        </div>

        {/* Champs personnalisés */}
        <div className="rounded-2xl bg-white ring-1 ring-gray-200 p-5">
          <div className="flex items-center gap-2 text-violet-600">
            <Sliders className="w-5 h-5" />
            <span className="text-sm font-medium text-gray-600">Champs personnalisés</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {loading ? '—' : (stats?.customFields || 0)}
          </p>
          <Button variant="ghost" size="sm" className="text-violet-600 hover:text-violet-700 p-0 h-auto mt-1" onClick={handleNavigateToCustomFields}>
            Gérer les champs
          </Button>
        </div>

        {/* Actions rapides */}
        <div className="md:col-span-2 rounded-2xl bg-white ring-1 ring-gray-200 p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Actions rapides</p>
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              title="Ajouter un contact"
              aria-label="Ajouter un contact"
              onClick={handleNavigateToContacts}
              className="group flex items-center justify-center py-4 rounded-xl bg-blue-50 hover:bg-blue-100 ring-1 ring-blue-100 hover:ring-blue-300 transition-all"
            >
              <span className="relative inline-flex w-12 h-12 rounded-full bg-white ring-1 ring-blue-100 items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                <User className="w-6 h-6 text-blue-600" />
                <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-blue-600 text-white ring-2 ring-white flex items-center justify-center">
                  <Plus className="w-3 h-3" />
                </span>
              </span>
            </button>
            <button
              type="button"
              title="Ajouter une entreprise"
              aria-label="Ajouter une entreprise"
              onClick={handleNavigateToCompanies}
              className="group flex items-center justify-center py-4 rounded-xl bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-100 hover:ring-emerald-300 transition-all"
            >
              <span className="relative inline-flex w-12 h-12 rounded-full bg-white ring-1 ring-emerald-100 items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                <Building2 className="w-6 h-6 text-emerald-600" />
                <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-emerald-600 text-white ring-2 ring-white flex items-center justify-center">
                  <Plus className="w-3 h-3" />
                </span>
              </span>
            </button>
            <button
              type="button"
              title="Exporter"
              aria-label="Exporter"
              onClick={handleNavigateToExport}
              className="group flex items-center justify-center py-4 rounded-xl bg-orange-50 hover:bg-orange-100 ring-1 ring-orange-100 hover:ring-orange-300 transition-all"
            >
              <span className="inline-flex w-12 h-12 rounded-full bg-white ring-1 ring-orange-100 items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                <FileDown className="w-6 h-6 text-orange-600" />
              </span>
            </button>
          </div>
        </div>

        {/* Derniers contacts ajoutés */}
        <div className="md:col-span-2 rounded-2xl bg-white ring-1 ring-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Derniers contacts ajoutés</h3>
            <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 p-0 h-auto text-xs" onClick={handleNavigateToContacts}>
              Tout voir
            </Button>
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 gap-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex items-center gap-3 p-2">
                  <div className="animate-pulse bg-gray-200 w-9 h-9 rounded-full"></div>
                  <div className="flex-1">
                    <div className="animate-pulse bg-gray-200 h-4 w-32 mb-1 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-3 w-24 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentContacts.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-2">
              {recentContacts.map((contact, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => navigate(`/contacts?open=${contact.id}`)}
                  title="Ouvrir la fiche du contact"
                  className="w-full text-left flex items-center gap-3 p-2 rounded-lg bg-gray-50 hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 transition-all cursor-pointer"
                >
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-blue-600 text-white text-xs font-medium">
                      {getInitials(contact.first_name, contact.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{`${contact.first_name} ${contact.last_name}`}</p>
                    <p className="text-xs text-gray-500 truncate">{contact.company_name || 'Aucune entreprise'}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">Aucun contact ajouté récemment</p>
          )}
        </div>

        {/* Dernières entreprises ajoutées */}
        <div className="rounded-2xl bg-white ring-1 ring-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 text-sm mb-3">Dernières entreprises ajoutées</h3>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="animate-pulse bg-gray-200 w-8 h-8 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="animate-pulse bg-gray-200 h-3.5 w-28 mb-1 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-3 w-16 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentCompanies.length > 0 ? (
            <div className="space-y-2">
              {recentCompanies.map((company, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => navigate(`/companies?open=${company.id}`)}
                  title="Ouvrir la fiche de l'entreprise"
                  className="w-full text-left flex items-center gap-2 p-1.5 -m-1.5 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center text-sm font-bold shrink-0">
                    {getCompanyInitial(company.name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{company.name}</p>
                    <p className="text-xs text-gray-400">{company.contacts_count} contact{company.contacts_count > 1 ? 's' : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">Aucune entreprise ajoutée récemment</p>
          )}
        </div>
      </div>
    </div>
  )
} 